import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ArrowLeft, Clock, ExternalLink, Newspaper, Link2, X as XIcon } from "lucide-react";
import SocialShareButtons from "../components/SocialShareButtons";
import { readSsgInitialData } from "../hooks/useSsgInitialData";

const BASE_URL = "https://allwhufc.com";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function NewsArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  // Hydrate from SSG shell so JS-rendering Googlebot never sees a
  // spinner-only DOM (which caused Soft 404s in Search Console).
  const initial = readSsgInitialData(slug);
  // Blogs have `content` field; external news has `headline`.
  const initialBlog = initial && initial.content ? initial : null;
  const initialArticle = initial && initial.headline && !initial.content ? initial : null;
  const [article, setArticle] = useState(initialArticle);
  const [blog, setBlog] = useState(initialBlog);
  const [related, setRelated] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [teamFilter, setTeamFilter] = useState(null); // null = default (article's team)
  const [categoryFilter, setCategoryFilter] = useState(null); // null = any
  const [loading, setLoading] = useState(!(initialBlog || initialArticle));
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    // Reset state on slug change so stale prior-slug content never bleeds
    // into a new URL.
    const fresh = readSsgInitialData(slug);
    const freshBlog = fresh && fresh.content ? fresh : null;
    const freshArticle = fresh && fresh.headline && !fresh.content ? fresh : null;
    setBlog(freshBlog);
    setArticle(freshArticle);
    setLoading(!(freshBlog || freshArticle));
    setNotFound(false);
    setRelated([]);
    setTeamFilter(null);
    setCategoryFilter(null);
    // Try blog first (richer owned content), fall back to external news article.
    (async () => {
      try {
        const res = await axios.get(`${API}/blogs/by-slug/${slug}`);
        if (alive) {
          setBlog(res.data);
          setArticle(null);
          setLoading(false);
        }
        return;
      } catch (_e) { /* fall through to news lookup */ }
      try {
        const res = await axios.get(`${API}/news/by-slug/${slug}`);
        if (alive) {
          setArticle(res.data);
          setBlog(null);
          setLoading(false);
        }
      } catch (err) {
        if (!alive) return;
        // Only flag notFound on a real 404 from the news endpoint.
        if (err?.response?.status === 404) {
          setNotFound(true);
        }
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [slug]);

  // Refetch related whenever filters or slug change (after article loads)
  useEffect(() => {
    if (!article) return;
    let alive = true;
    setRelatedLoading(true);
    const params = new URLSearchParams({ limit: "4" });
    if (teamFilter) params.set("team", teamFilter);
    if (categoryFilter) params.set("category", categoryFilter);
    axios
      .get(`${API}/news/${slug}/related?${params.toString()}`)
      .then((res) => alive && setRelated(res.data?.related || []))
      .catch(() => alive && setRelated([]))
      .finally(() => alive && setRelatedLoading(false));
    return () => {
      alive = false;
    };
  }, [article, slug, teamFilter, categoryFilter]);

  if (loading || (!article && !blog && !notFound)) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <SEO title="Loading..." path={`/news/${slug}`} />
        <div className="flex items-center justify-center py-40">
          <div className="spinner" />
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="news-article-notfound">
        <Navbar />
        <SEO title="Article not found" path={`/news/${slug}`} noindex={true} />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <Newspaper className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-700 mb-2" style={{ fontFamily: "Barlow Condensed" }}>
            ARTICLE NOT FOUND
          </h1>
          <p className="text-slate-500 mb-6">The article you requested could not be found.</p>
          <Link to="/news" className="inline-flex items-center gap-2 text-[#2BAEE5] font-medium hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to all news
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Blog rendering
  if (blog) {
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: blog.title,
      datePublished: blog.published_at,
      dateModified: blog.updated_at || blog.published_at,
      author: { "@type": "Organization", name: blog.author || "ALLWHUFC" },
      publisher: {
        "@type": "Organization",
        name: "All WHUFC",
        logo: { "@type": "ImageObject", url: `${BASE_URL}/og-image.png` },
      },
      mainEntityOfPage: `${BASE_URL}/news/${blog.slug}`,
      image: blog.images?.[0] ? [blog.images[0]] : undefined,
      description: (blog.content || "").slice(0, 300),
    };
    const description = (blog.content || "").slice(0, 160).replace(/\s+/g, " ").trim();
    return (
      <div className="min-h-screen bg-slate-50" data-testid="news-article-page">
        <SEO
          title={blog.title}
          path={`/news/${blog.slug}`}
          description={description || `${blog.title} — ALLWHUFC original article.`}
          type="article"
          image={blog.images?.[0]}
          jsonLd={jsonLd}
          seoTitle={blog.seo_title}
          seoDescription={blog.seo_description}
          keywords={blog.seo_keywords}
          imageAlt={blog.image_alt}
        />
        <Navbar />
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#7C2C3B] mb-6"
            data-testid="article-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <header className="mb-6">
            <div className="text-xs font-semibold text-[#7C2C3B] uppercase tracking-wide mb-2">ALLWHUFC Original</div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] leading-tight mb-3" style={{ fontFamily: "Barlow Condensed" }}>
              {blog.title}
            </h1>
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <span className="font-medium text-[#7C2C3B]">{blog.author || "ALLWHUFC"}</span>
              {blog.published_at && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {formatDate(blog.published_at)}
                </span>
              )}
            </div>
          </header>
          {blog.images?.[0] && (
            <img src={blog.images[0]} alt={blog.title} className="w-full rounded-lg mb-6 object-cover max-h-[420px]" />
          )}
          <div className="prose prose-slate max-w-none text-slate-700 whitespace-pre-wrap leading-relaxed">
            {blog.content}
          </div>
          {blog.images?.[1] && (
            <img src={blog.images[1]} alt="" className="w-full rounded-lg my-6 object-cover max-h-[420px]" />
          )}
          {blog.links?.length > 0 && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <h3 className="text-sm font-semibold text-slate-500 uppercase mb-3">Related Links</h3>
              <div className="flex flex-wrap gap-3">
                {blog.links.map((link, i) =>
                  link.url ? (
                    <a
                      key={i}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-[#2BAEE5] hover:underline font-medium"
                    >
                      <Link2 className="w-3 h-3" /> {link.text || link.url}
                    </a>
                  ) : null
                )}
              </div>
            </div>
          )}
          <div className="mt-8">
            <SocialShareButtons shareText={blog.title} />
          </div>
        </article>
        <Footer />
      </div>
    );
  }

  // External news article rendering (aggregated)
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: article.headline,
    datePublished: article.published_at,
    dateModified: article.updated_at || article.published_at,
    author: { "@type": "Organization", name: article.source || "All WHUFC" },
    publisher: {
      "@type": "Organization",
      name: "All WHUFC",
      logo: { "@type": "ImageObject", url: `${BASE_URL}/og-image.png` },
    },
    mainEntityOfPage: `${BASE_URL}/news/${article.slug}`,
    image: article.image_url ? [article.image_url] : undefined,
    description: (article.summary || article.headline || "").slice(0, 300),
  };
  const description = (article.summary || article.headline || "").slice(0, 160).replace(/\s+/g, " ").trim();
  return (
    <div className="min-h-screen bg-slate-50" data-testid="news-article-page">
      <SEO
        title={article.headline}
        path={`/news/${article.slug}`}
        description={description}
        type="article"
        image={article.image_url}
        jsonLd={jsonLd}
        seoTitle={article.seo_title}
        seoDescription={article.seo_description}
        keywords={article.seo_keywords}
        imageAlt={article.image_alt}
      />
      <Navbar />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#7C2C3B] mb-6"
          data-testid="article-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <header className="mb-6">
          <div className="text-xs font-semibold text-[#7C2C3B] uppercase tracking-wide mb-2">
            {article.category === "review" ? "Match Review" : "News"} · {article.team === "women" ? "Women's Team" : "Men's Team"}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] leading-tight mb-3" style={{ fontFamily: "Barlow Condensed" }}>
            {article.headline}
          </h1>
          <div className="flex items-center gap-3 text-sm text-slate-500">
            <span className="font-medium text-[#7C2C3B]">{article.source}</span>
            {article.published_at && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> {formatDate(article.published_at)}
              </span>
            )}
          </div>
        </header>
        {article.image_url && (
          <img src={article.image_url} alt={article.headline} className="w-full rounded-lg mb-6 object-cover max-h-[420px]" />
        )}
        {article.summary && (
          <div className="text-lg text-slate-700 leading-relaxed mb-6 whitespace-pre-wrap">{article.summary}</div>
        )}
        {article.long_summary && (
          <section className="mb-8" data-testid="the-story-so-far">
            <h2 className="text-xl font-bold text-slate-900 mb-3">The Story So Far</h2>
            <p className="text-slate-800 leading-relaxed whitespace-pre-wrap">{article.long_summary}</p>
          </section>
        )}
        {article.source_url && (
          <a
            href={article.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#7C2C3B] hover:bg-[#63232F] text-white px-5 py-3 rounded-lg font-semibold transition-colors"
            data-testid="article-read-source"
          >
            Read the full article at {article.source}
            <ExternalLink className="w-4 h-4" />
          </a>
        )}

        {(related.length > 0 || relatedLoading || teamFilter || categoryFilter) && (
          <section className="mt-12" data-testid="related-articles-section">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Newspaper className="w-5 h-5 text-[#7C2C3B]" />
              <h2 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: "Barlow Condensed" }}>
                RELATED ARTICLES
              </h2>
              <span className="text-xs text-slate-500">Explore more</span>
            </div>

            {/* Filter pills */}
            <div className="flex flex-wrap gap-3 mb-4 items-center">
              <div className="flex items-center gap-1">
                <span className="text-[11px] text-slate-500 uppercase tracking-wide mr-1">Team:</span>
                {[
                  { k: null, label: "Default" },
                  { k: "men", label: "Men's" },
                  { k: "women", label: "Women's" },
                ].map(({ k, label }) => {
                  const active = teamFilter === k;
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => setTeamFilter(k)}
                      data-testid={`related-team-filter-${k || "default"}`}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        active
                          ? "bg-[#7C2C3B] text-white border-[#7C2C3B]"
                          : "bg-white text-slate-600 border-slate-200 hover:border-[#7C2C3B] hover:text-[#7C2C3B]"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                <span className="text-[11px] text-slate-500 uppercase tracking-wide mr-1">Category:</span>
                {[
                  { k: null, label: "All" },
                  { k: "latest", label: "Latest" },
                  { k: "review", label: "Reviews" },
                  { k: "transfer", label: "Transfers" },
                  { k: "social", label: "Social" },
                ].map(({ k, label }) => {
                  const active = categoryFilter === k;
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => setCategoryFilter(k)}
                      data-testid={`related-category-filter-${k || "all"}`}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        active
                          ? "bg-[#2BAEE5] text-white border-[#2BAEE5]"
                          : "bg-white text-slate-600 border-slate-200 hover:border-[#2BAEE5] hover:text-[#2BAEE5]"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Active filters summary chip */}
            {(teamFilter || categoryFilter) && (
              <div className="mb-4 flex items-center gap-2" data-testid="related-active-filters">
                <span className="text-[11px] text-slate-500 uppercase tracking-wide">Active:</span>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#7C2C3B]/10 border border-[#7C2C3B]/30 text-[#7C2C3B] text-xs font-medium">
                  <span>
                    {[
                      teamFilter === "men" ? "Men's" : teamFilter === "women" ? "Women's" : null,
                      categoryFilter
                        ? {
                            latest: "Latest",
                            review: "Reviews",
                            transfer: "Transfers",
                            social: "Social",
                          }[categoryFilter] || categoryFilter
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setTeamFilter(null);
                      setCategoryFilter(null);
                    }}
                    aria-label="Clear filters"
                    data-testid="related-clear-filters"
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-[#7C2C3B]/20 transition-colors"
                  >
                    <XIcon className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}

            {relatedLoading ? (
              <div className="text-sm text-slate-400 py-4">Loading related articles…</div>
            ) : related.length === 0 ? (
              <div className="text-sm text-slate-500 py-4 bg-slate-50 rounded-lg px-4 border border-dashed border-slate-200">
                No articles match these filters yet. Try a different team or category.
              </div>
            ) : (              <div className="grid sm:grid-cols-2 gap-3">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={`/news/${r.slug}`}
                    className="group block bg-white border border-slate-200 rounded-lg p-4 hover:border-[#2BAEE5] hover:shadow-md transition-all"
                    data-testid={`related-article-${r.slug}`}
                  >
                    <h3 className="font-semibold text-[#0F172A] leading-tight mb-2 group-hover:text-[#7C2C3B] line-clamp-2">
                      {r.headline}
                    </h3>
                    {r.summary && (
                      <p className="text-xs text-slate-600 leading-relaxed line-clamp-2 mb-2">{r.summary}</p>
                    )}
                    <div className="flex items-center justify-between text-[11px] text-slate-500">
                      <span className="font-medium text-[#7C2C3B]">{r.source}</span>
                      {r.published_at && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(r.published_at).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                          })}
                        </span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        )}

        <div className="mt-10 pt-6 border-t border-slate-200">
          <SocialShareButtons shareText={article.headline} />
        </div>
      </article>
      <Footer />
    </div>
  );
}
